// Live-tunable parameters. Mutated by the debug panel.
export const SpringParams = {
  // Restoring stiffness. ω₀ = √k → frequency Hz = √k / (2π).
  // Default 800 → ~4.5 Hz. Slider can pump this up to a fast 30+ Hz buzz.
  k: 800,
  // Damping while finger is on the line.
  cHeld: 10,
  // Damping after release. ζ ≈ c / (2√k) — keep ζ small so the line
  // visibly oscillates a few cycles before settling.
  cReleased: 4.5,
  // Strength of the finger-tracking pull while held.
  kPull: 1200,
};

const REST_X_EPS = 0.05;
const REST_V_EPS = 0.5;

export class Spring {
  x = 0;
  vx = 0;
  private targetX: number | null = null;
  private maxX: number;

  constructor(maxDisplacement: number) {
    this.maxX = maxDisplacement;
  }

  setMaxDisplacement(maxDisplacement: number) {
    this.maxX = maxDisplacement;
  }

  hold(targetX: number) {
    this.targetX = targetX;
  }

  release() {
    this.targetX = null;
  }

  /** One-shot velocity impulse — used for the initial "pluck" on touch-down. */
  kick(velocity: number) {
    this.vx += velocity;
  }

  step(dtSec: number) {
    const dt = Math.min(dtSec, 0.033);
    const held = this.targetX !== null;
    const c = held ? SpringParams.cHeld : SpringParams.cReleased;
    let a = -SpringParams.k * this.x - c * this.vx;
    if (held) {
      a += SpringParams.kPull * (this.targetX! - this.x);
    }
    this.vx += a * dt;
    this.x += this.vx * dt;
    if (this.x > this.maxX) {
      this.x = this.maxX;
      if (this.vx > 0) this.vx = 0;
    } else if (this.x < -this.maxX) {
      this.x = -this.maxX;
      if (this.vx < 0) this.vx = 0;
    }
    if (!held && Math.abs(this.x) < REST_X_EPS && Math.abs(this.vx) < REST_V_EPS) {
      this.x = 0;
      this.vx = 0;
    }
  }

  get isAtRest(): boolean {
    return this.targetX === null && this.x === 0 && this.vx === 0;
  }
}
